import { jsx as _jsx } from "react/jsx-runtime";
import { addErrorNotification, configurationSettings, getRemainingOrderTicketCountForShowtime, orderHasInProgressAction, TicketRedemptionCodeCategory } from '@vista/omnichannel-components-domain';
import { bemClassBuilder } from '@vista-digital/bem-class-builder';
import { useUpdateEffect } from '@vista-digital/react-tools';
import { observer } from 'mobx-react';
import { forwardRef, useMemo, useReducer } from 'react';
import { useShowtimeContext } from '../../../context/showtime/hooks/use-showtime-context';
import { useFormValidator } from '../../../hooks/use-form-validator';
import { Form } from '../../../subcomponents';
import { withComponentWrapper } from '../../../utils/with-component-wrapper';
import { TicketRedemptionCodeFormActionType as ActionType } from './types/action-type';
import { TicketRedemptionCodeFormContextInstance } from './types/context';
import { defaultTicketRedemptionCodeFormProps, ticketRedemptionCodeFormPropsValidator } from './types/props';
import { defaultTicketRedemptionCodeFormState } from './types/state';
import { ticketRedemptionCodeFormActionReducer } from './utils/action-reducer';
import { getNotificationDetailsForError } from './utils/get-notification-details-for-error';
import { getSelectedType } from './utils/get-selected-type';
import { getTypesForCode } from './utils/get-types-for-code';
import { getValidationConfig } from './utils/get-validation-config';
import { handleSubmit } from './utils/handle-submit';
import { isCodeLongEnough } from './utils/is-code-long-enough';
const b = bemClassBuilder('v-ticket-redemption-code-form');
const TicketRedemptionCodeFormBase = (props, ref) => {
    const { config, displayText, events, formId, children } = props;
    const showtimeContext = useShowtimeContext();
    const { showtime, showtimeId } = showtimeContext;
    const [state, dispatch] = useReducer(ticketRedemptionCodeFormActionReducer, defaultTicketRedemptionCodeFormState);
    const { formData, helpTextsById } = state;
    const { ticketRedemptionCodeTypes } = configurationSettings.data;
    const filteredTypes = ticketRedemptionCodeTypes.filter(type => type.category !== TicketRedemptionCodeCategory.MemberSubscriptionCard // We do not support adding member subscription cards using this component.
    );
    const codeLongEnough = isCodeLongEnough(formData.code, config.validation);
    const selectedType = getSelectedType(filteredTypes, formData.typeId);
    const validationConfig = getValidationConfig(config.validation, codeLongEnough, selectedType);
    const formValidatorState = useFormValidator(validationConfig);
    const { detectedTypeId, sortedTypes } = getTypesForCode(filteredTypes, formData.code);
    useUpdateEffect(() => dispatch({ type: ActionType.SetDetectedType, typeId: detectedTypeId }), [detectedTypeId]);
    useUpdateEffect(() => dispatch({ type: ActionType.Reset }), [showtimeId]);
    useUpdateEffect(() => {
        if (!selectedType)
            return;
        dispatch({ type: ActionType.SetFormData, formData: Object.assign(Object.assign({}, formData), { typeId: selectedType === null || selectedType === void 0 ? void 0 : selectedType.id }) });
    }, [codeLongEnough]);
    const orderProcessing = orderHasInProgressAction();
    const remainingTickets = getRemainingOrderTicketCountForShowtime(showtimeId);
    const isDisabled = remainingTickets === 0 || orderProcessing || state.isSubmitting;
    const context = useMemo(() => (Object.assign(Object.assign({}, formValidatorState), { onChange: (fieldName, value) => {
            dispatch({ type: ActionType.SetFormData, formData: Object.assign(Object.assign({}, formData), { [fieldName]: value }) });
        }, dispatch, codeTypes: sortedTypes, formData,
        validationConfig, disabled: isDisabled, loading: !showtime, config })), [config, formData, formValidatorState, isDisabled, showtime, sortedTypes, validationConfig]);
    const handleSubmitAndReset = async () => {
        await handleSubmit(selectedType, formData, showtimeContext, config);
        dispatch({ type: ActionType.Reset });
    };
    const handleFailedSubmit = error => {
        var _a;
        addErrorNotification(getNotificationDetailsForError(displayText, error), error);
        (_a = events.onAfterFailedSubmit) === null || _a === void 0 ? void 0 : _a.call(events, error);
    };
    const handleFormSubmittingStateChanged = (isSubmitting) => {
        var _a;
        dispatch({ type: ActionType.SetFormSubmittingState, isSubmitting });
        (_a = events.onFormSubmittingStateChanged) === null || _a === void 0 ? void 0 : _a.call(events, isSubmitting);
    };
    return (_jsx(Form, Object.assign({}, props, { id: formId, className: b.block({ additionalFieldsShown: codeLongEnough }), isLoading: !showtime, validator: () => formValidatorState.validator.validateForm(formData), events: Object.assign(Object.assign({}, events), { onSubmit: handleSubmitAndReset, onAfterFailedSubmit: handleFailedSubmit, onFormSubmittingStateChanged: handleFormSubmittingStateChanged }), helpTexts: Object.values(helpTextsById), setFormData: getMergedData => {
            dispatch({ type: ActionType.SetFormData, formData: getMergedData(formData) });
        }, ref: ref }, { children: _jsx(TicketRedemptionCodeFormContextInstance.Provider, Object.assign({ value: context }, { children: children })) })));
};
const TicketRedemptionCodeForm = observer(forwardRef(TicketRedemptionCodeFormBase));
TicketRedemptionCodeForm.displayName = 'TicketRedemptionCodeForm';
/**
 * The **Ticket Redemption Code Form** component allows the user to add ticket redemption codes to the order that can
 * be redeemed for tickets.
 */
export const TicketRedemptionCodeFormComponent = withComponentWrapper(TicketRedemptionCodeForm, defaultTicketRedemptionCodeFormProps, ticketRedemptionCodeFormPropsValidator);
